import * as React from "react";

/**
 * Das dezente Live-Signal „hier arbeitet gerade jemand" an einem Formularfeld.
 *
 * Es ist ABSOLUT positioniert und liegt rechts in der Label-Zeile. Als weiteres
 * Kind von `.fld` (flex column, `gap: 7px`) würde es das Feld um ~23 px wachsen
 * lassen und die ganze Grid-Zeile auseinanderschieben — mitten in der Eingabe
 * des Lesers im Nachbarfeld. Ein Signal darf nicht am Layout ziehen.
 *
 * Es ist außerdem DAUERHAFT montiert und blendet nur die Deckkraft ein: so
 * erscheint und verschwindet es weich statt zu blitzen. Der zuletzt gesehene
 * Name bleibt während der Ausblendung stehen, damit der Text nicht mitten in
 * der Überblendung wegspringt.
 *
 * Die Optik steht inline: contactRedesign.css ist geteilt und wird in dieser
 * Welle von mehreren Änderungen angefasst.
 */
export const FeldPraesenz: React.FC<{
  /** Anzeigename des Kollegen, der das Feld gerade bearbeitet. */
  lockedBy?: string;
  /**
   * Am oberen Rand des Feldes ausrichten statt mittig — für Hüllen um ganze
   * Widgets, deren Label die erste Zeile ist ({@link FeldPraesenzHuelle}).
   */
  oben?: boolean;
}> = ({ lockedBy, oben }) => {
  const [name, setName] = React.useState(lockedBy);
  React.useEffect(() => {
    if (lockedBy) setName(lockedBy);
  }, [lockedBy]);

  const sichtbar = !!lockedBy;
  return (
    <span
      role="status"
      aria-hidden={!sichtbar}
      title={sichtbar ? `${lockedBy} bearbeitet dieses Feld gerade` : undefined}
      style={{
        position: "absolute",
        right: 0,
        top: oben ? 0 : "50%",
        transform: oben ? undefined : "translateY(-50%)",
        maxWidth: "70%",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        font: "500 12px/1.4 var(--ui)",
        color: "var(--ink-2)",
        // Deckt einen zu langen Label-Text sauber ab, statt ihn zu überlagern.
        background: "var(--surface)",
        paddingLeft: 8,
        pointerEvents: "none",
        opacity: sichtbar ? 1 : 0,
        transition: "opacity .18s ease",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "var(--ok)",
          flexShrink: 0,
        }}
      />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name ? `${name} bearbeitet gerade …` : null}
      </span>
    </span>
  );
};

/**
 * Label-Zeile eines Feldes samt Präsenz-Signal. Der Container ist der
 * Bezugsrahmen für die absolute Einblendung — deshalb steht sie hier und nicht
 * am `.fld` selbst: `.fld` ist geteiltes CSS, und ein `position: relative` dort
 * würde absolut positionierte Innereien fremder Widgets umhängen.
 */
export const FeldKopf: React.FC<{ label: React.ReactNode; lockedBy?: string }> = ({
  label,
  lockedBy,
}) => (
  <div style={{ position: "relative", display: "flex", alignItems: "center", minWidth: 0 }}>
    <label>{label}</label>
    <FeldPraesenz lockedBy={lockedBy} />
  </div>
);

/**
 * Präsenz für Felder, deren Widget kein `onFocus`/`onBlur` anbietet (TagInput,
 * BrancheMultiSelect). React-Fokusereignisse blasen auf (focusin/focusout),
 * deshalb hängt sie der ohnehin vorhandene `.fld`-Hüllcontainer ab — das
 * geteilte Widget bleibt unangetastet.
 */
export const FeldPraesenzHuelle: React.FC<{
  className?: string;
  lockedBy?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  children: React.ReactNode;
}> = ({ className = "fld", lockedBy, onFocus, onBlur, children }) => (
  <div
    className={className}
    style={{ position: "relative" }}
    onFocusCapture={onFocus}
    onBlurCapture={onBlur}
  >
    {children}
    <FeldPraesenz lockedBy={lockedBy} oben />
  </div>
);

export default FeldPraesenz;
